import { graphql } from "gatsby";
import { useTheme } from "@mui/material/styles";
import React from "react";
import {
  getDesktopSize,
  getLaptopSize,
  getMobileSize,
  getPosition,
  getProfileSize,
  getTabletLandscapeSize,
  getTabletPortraitSize,
  typeToObjectFitMap
} from "./utils";
import type { Props } from "./types";

const Image = (imageData: Props) => {
  const theme = useTheme();

  if ("src" in imageData) {
    return (
      <img
        className={imageData.className}
        draggable={false}
        style={{
          objectFit:
            imageData.size || typeToObjectFitMap[imageData.type || "Decorative"]
        }}
        decoding="async"
        loading={imageData.loading}
        src={imageData.src}
        alt={imageData.alt}
        data-testid={imageData["data-testid"]}
      />
    );
  }

  const {
    altText,
    sizes,
    className,
    size,
    type,
    position,
    focalX,
    focalY,
    isMobile,
    loading = "lazy",
    "data-testid": dataTestId
  } = imageData;

  const profileSize = getProfileSize(sizes);
  if (profileSize) {
    return (
      <img
        className={imageData.className}
        draggable={false}
        style={{
          objectFit:
            imageData.size ||
            typeToObjectFitMap[imageData.type || "Decorative"],
          position: "absolute",
          height: "100%",
          width: "100%",
          inset: 0
        }}
        decoding="async"
        loading={imageData.loading}
        src={profileSize.url}
        alt={imageData.altText}
        data-testid={imageData["data-testid"]}
      />
    );
  }

  const mobile = getMobileSize(sizes);
  const tabletPortrait = getTabletPortraitSize(sizes);
  const tabletLandscape = getTabletLandscapeSize(sizes);
  const laptop = getLaptopSize(sizes);
  const desktop = getDesktopSize(sizes);

  if (!mobile || !tabletPortrait || !tabletLandscape || !laptop || !desktop) {
    return (
      <img
        className={imageData.className}
        draggable={false}
        style={{
          objectFit:
            imageData.size || typeToObjectFitMap[imageData.type || "Decorative"]
        }}
        decoding="async"
        loading={imageData.loading}
        src={imageData.url}
        alt={imageData.altText}
        data-testid={imageData["data-testid"]}
      />
    );
  }

  return (
    <img
      className={className}
      draggable={false}
      style={{
        objectFit: size || typeToObjectFitMap[type || "Decorative"],
        objectPosition: getPosition({
          size,
          position,
          focalX,
          focalY,
          isMobile
        })
      }}
      decoding="async"
      loading={loading}
      src={desktop.url}
      srcSet={`${mobile.url} ${mobile.width}w, ${tabletPortrait.url} ${tabletPortrait.width}w, ${tabletLandscape.url} ${tabletLandscape.width}w, ${laptop.url} ${laptop.width}w, ${desktop.url} ${desktop.width}w`}
      sizes={`(max-width: ${theme.breakpoints.values.sm}px) ${mobile.width}px, (max-width: ${theme.breakpoints.values.md}px) ${tabletPortrait.width}px, (max-width: ${theme.breakpoints.values.lg}px) ${tabletLandscape.width}px, (max-width: ${theme.breakpoints.values.xl}px) ${laptop.width}px, ${desktop.width}px`}
      alt={altText}
      data-testid={dataTestId}
    />
  );
};

export default Image;

export const query = graphql`
  fragment BaseImageFragment on PayloadImage {
    type
    altText
    type
    focalX
    focalY
    filename
    url
  }

  fragment ImageCardFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      cardMobile {
        width
        url
      }
      cardTabletPortrait {
        width
        url
      }
      cardTabletLandscape {
        width
        url
      }
      cardLaptop {
        width
        url
      }
      cardDesktop {
        width
        url
      }
    }
  }

  # Although this is used, the resulting data is never used anywhere
  fragment ImageDocumentFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      documentMobile {
        width
        url
      }
      documentTabletPortrait {
        width
        url
      }
      documentTabletLandscape {
        width
        url
      }
      documentLaptop {
        width
        url
      }
      documentDesktop {
        width
        url
      }
    }
  }

  fragment ImageGallerySlideFragment on PayloadImage {
    ...BaseImageFragment
    collectionType
    caption
    sizes {
      gallerySlideMobile {
        width
        url
      }
      gallerySlideTabletPortrait {
        width
        url
      }
      gallerySlideTabletLandscape {
        width
        url
      }
      gallerySlideLaptop {
        width
        url
      }
      gallerySlideDesktop {
        width
        url
      }
      thumbnail {
        width
        url
      }
    }
  }

  fragment ImageHeaderFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      headerMobile {
        width
        url
      }
      headerTabletPortrait {
        width
        url
      }
      headerTabletLandscape {
        width
        url
      }
      headerLaptop {
        width
        url
      }
      headerDesktop {
        width
        url
      }
    }
  }

  fragment ImageHeroFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      heroMobile {
        width
        url
      }
      heroTabletPortrait {
        width
        url
      }
      heroTabletLandscape {
        width
        url
      }
      heroLaptop {
        width
        url
      }
      heroDesktop {
        width
        url
      }
    }
  }

  fragment ImageProfileFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      profile {
        width
        url
      }
    }
  }

  fragment ImageRichTextFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      richTextMobile {
        width
        url
      }
      richTextTabletPortrait {
        width
        url
      }
      richTextTabletLandscape {
        width
        url
      }
      richTextLaptop {
        width
        url
      }
      richTextDesktop {
        width
        url
      }
    }
  }

  fragment ImageSlideFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      slideMobile {
        width
        url
      }
      slideTabletPortrait {
        width
        url
      }
      slideTabletLandscape {
        width
        url
      }
      slideLaptop {
        width
        url
      }
      slideDesktop {
        width
        url
      }
    }
  }

  fragment ImageTrainingDetailFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      trainingDetailMobile {
        url
      }
      trainingDetailTabletPortrait {
        url
      }
      trainingDetailTabletLandscape {
        url
      }
      trainingDetailLaptop {
        url
      }
      trainingDetailDesktop {
        url
      }
    }
  }

  fragment ImageVideoFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      videoMobile {
        url
      }
      videoTabletPortrait {
        url
      }
      videoTabletLandscape {
        url
      }
      videoLaptop {
        url
      }
      videoDesktop {
        url
      }
    }
  }

  fragment ImageVillainFragment on PayloadImage {
    ...BaseImageFragment
    sizes {
      villainMobile {
        width
        url
      }
      villainTabletPortrait {
        width
        url
      }
      villainTabletLandscape {
        width
        url
      }
      villainLaptop {
        width
        url
      }
      villainDesktop {
        width
        url
      }
    }
  }
`;
